import { useEffect, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { FormModal } from "../../components/common/FormModal";
import { StatusBadge } from "../../components/common/StatusBadge";
import { TextField, SelectField } from "../../components/common/Form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BiteProductsService, BiteCategoriesService } from "../../services";
import { currency } from "../../utils/format";
import { ImageOff, ImagePlus, Pencil, Plus, Power } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "../../hooks/useCan";

const empty = {
  biteCategoryId: "",
  biteName: "",
  barcode: "",
  sellingPrice: 0,
  itemSection: "FOOD",
  stockTracked: false,
  requiresKot: false,
  costPrice: 0,
  lowStockLevel: 0,
};

const OUTLETS = [
  { code: "BAR", name: "BAR" },
  { code: "RESTAURANT", name: "RESTAURANT" },
];

const outletSettingsByCode = (settings = []) =>
  Object.fromEntries(settings.map((setting) => [setting.outletCode, setting]));

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const formatImageFileSize = (size) => {
  if (!size) {
    return "";
  }

  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const ProductImagePicker = ({
  previewUrl,
  selectedFile,
  existingImageUrl,
  markedForRemoval,
  onSelectFile,
  onClearSelectedFile,
  onRemoveExistingImage,
  disabled,
}) => (
  <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex flex-col md:flex-row gap-4">
      <div className="h-32 w-full md:w-40 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center shrink-0">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Restaurant item preview"
            className="h-full w-full object-contain p-2"
          />
        ) : (
          <div className="text-center text-slate-400 px-3">
            <ImageOff className="h-8 w-8 mx-auto mb-1" />
            <div className="text-xs font-medium">No image</div>
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800">
          Product image
        </div>

        <p className="text-xs text-slate-500 mt-1">
          Select an image from your computer. You can preview it before saving.
          JPG, PNG and WEBP are supported up to 10 MB.
        </p>

        {selectedFile && (
          <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-700">
            Selected: {selectedFile.name} {formatImageFileSize(selectedFile.size) && `(${formatImageFileSize(selectedFile.size)})`}
          </div>
        )}

        {!selectedFile && existingImageUrl && !markedForRemoval && (
          <div className="mt-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-700">
            Current saved image will be kept unless you replace or remove it.
          </div>
        )}

        {markedForRemoval && (
          <div className="mt-2 rounded-lg bg-rose-50 border border-rose-100 px-3 py-2 text-xs text-rose-700">
            Current image will be removed when you save.
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-md bg-navy-800 hover:bg-navy-900 text-white text-xs font-semibold px-3 py-2 cursor-pointer">
            <ImagePlus className="h-4 w-4" />
            Select Image
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className="hidden"
              disabled={disabled}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                onSelectFile(file);
              }}
            />
          </label>

          {selectedFile && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClearSelectedFile}
              disabled={disabled}
            >
              Clear Selected
            </Button>
          )}

          {!selectedFile && existingImageUrl && !markedForRemoval && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRemoveExistingImage}
              disabled={disabled}
              className="text-rose-700 hover:text-rose-800"
            >
              Remove Image
            </Button>
          )}
        </div>
      </div>
    </div>
  </div>
);

const StockBadge = ({ value }) => {
  const tracked = Boolean(value);

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${
        tracked
          ? "bg-sky-50 text-sky-700 border-sky-100"
          : "bg-slate-50 text-slate-600 border-slate-200"
      }`}
    >
      {tracked ? "Tracked" : "No Stock"}
    </span>
  );
};

export const BiteProductsPage = () => {
  const can = useCan();

  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [outletSettings, setOutletSettings] = useState(null);
  const [outletLoading, setOutletLoading] = useState(false);
  const [outletLoadError, setOutletLoadError] = useState("");
  const [outletSaving, setOutletSaving] = useState({});
  const [outletSaveErrors, setOutletSaveErrors] = useState({});
  const [createdProductId, setCreatedProductId] = useState(null);

  const load = () => {
    setLoading(true);

    Promise.all([BiteProductsService.list(), BiteCategoriesService.list()])
      .then(([p, c]) => {
        setRows(p);
        setCats(c);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const loadOutletSettings = async (biteProductId) => {
    if (!biteProductId) return;
    setOutletLoading(true);
    setOutletLoadError("");
    setOutletSettings(null);
    try {
      const settings = await BiteProductsService.getOutletSettings(biteProductId);
      if (!Array.isArray(settings) || OUTLETS.some(({ code }) => !settings.some((item) => item.outletCode === code))) {
        throw new Error("Outlet settings response was incomplete.");
      }
      setOutletSettings(outletSettingsByCode(settings));
    } catch (e) {
      setOutletLoadError(e?.response?.data?.message || e?.message || "Failed to load outlet settings.");
    } finally {
      setOutletLoading(false);
    }
  };

  const openCreate = () => {
    setForm(empty);
    setEditingId(null);
    setErrors({});
    setImageFile(null);
    setImagePreviewUrl("");
    setExistingImageUrl("");
    setRemoveImage(false);
    setCreatedProductId(null);
    setOutletSettings({
      BAR: { outletCode: "BAR", enabled: true, sellingPriceOverride: null },
      RESTAURANT: { outletCode: "RESTAURANT", enabled: false, sellingPriceOverride: null },
    });
    setOutletLoading(false);
    setOutletLoadError("");
    setOutletSaveErrors({});
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.biteId);

    setForm({
      biteCategoryId: r.biteCategoryId ?? "",
      biteName: r.biteName || "",
      barcode: r.barcode || "",
      sellingPrice: r.sellingPrice ?? 0,
      itemSection: r.itemSection || "FOOD",
      stockTracked: Boolean(r.stockTracked),
      requiresKot: Boolean(r.requiresKot),
      costPrice: r.costPrice ?? 0,
      lowStockLevel: r.lowStockLevel ?? 0,
    });

    setErrors({});
    setImageFile(null);
    setImagePreviewUrl(r.imageUrl || "");
    setExistingImageUrl(r.imageUrl || "");
    setRemoveImage(false);
    setOutletSettings(null);
    setCreatedProductId(null);
    setOutletLoadError("");
    setOutletSaveErrors({});
    setOpen(true);
    loadOutletSettings(r.biteId);
  };

  const handleModalOpenChange = (nextOpen) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setImageFile(null);
      setImagePreviewUrl("");
      setExistingImageUrl("");
      setRemoveImage(false);
    }
  };

  const handleImageFileSelect = (file) => {
    if (!file) {
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error("Image file size must not exceed 10 MB");
      return;
    }

    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const clearSelectedImageFile = () => {
    setImageFile(null);
    setImagePreviewUrl(existingImageUrl || "");
    setRemoveImage(false);
  };

  const markExistingImageForRemoval = () => {
    setImageFile(null);
    setImagePreviewUrl("");
    setRemoveImage(Boolean(existingImageUrl));
  };

  const resolveSavedBiteId = (response) => {
    return (
      editingId ||
      response?.biteId ||
      response?.data?.biteId ||
      response?.data?.data?.biteId ||
      response?.data?.data?.data?.biteId ||
      null
    );
  };

  const validateOutletOverrides = () => {
    const parsed = {};
    const nextErrors = {};
    OUTLETS.forEach(({ code }) => {
      const raw = outletSettings?.[code]?.sellingPriceOverride;
      if (raw === "" || raw == null) {
        parsed[code] = null;
      } else {
        const value = Number(raw);
        if (!Number.isFinite(value) || value < 0) {
          nextErrors[code] = "Price override must be blank or a number greater than or equal to 0.";
        } else {
          parsed[code] = value;
        }
      }
    });
    setOutletSaveErrors(nextErrors);
    return Object.keys(nextErrors).length === 0 ? parsed : null;
  };

  const persistOutletSettings = async (biteProductId, parsedOverrides) => {
    setOutletSaving({ BAR: true, RESTAURANT: true });
    setOutletSaveErrors({});
    const results = await Promise.allSettled(
      OUTLETS.map(({ code }) => {
        const current = outletSettings[code];
        return BiteProductsService.updateOutletSetting(biteProductId, code, {
          enabled: Boolean(current.enabled),
          sellingPriceOverride: parsedOverrides[code],
        });
      })
    );

    const failed = [];
    const nextSettings = { ...outletSettings };
    const nextErrors = {};
    results.forEach((result, index) => {
      const { code } = OUTLETS[index];
      if (result.status === "fulfilled") {
        const request = {
          enabled: Boolean(outletSettings[code].enabled),
          sellingPriceOverride: parsedOverrides[code],
        };
        nextSettings[code] = {
          ...outletSettings[code],
          ...(result.value || {}),
          ...request,
          effectiveSellingPrice:
            result.value?.effectiveSellingPrice ??
            (parsedOverrides[code] ?? Number(form.sellingPrice || 0)),
        };
      } else {
        const message = result.reason?.response?.data?.message || result.reason?.message || `Failed to save ${code} settings.`;
        failed.push(`${code}: ${message}`);
        nextErrors[code] = message;
      }
    });
    setOutletSettings(nextSettings);
    setOutletSaveErrors(nextErrors);
    setOutletSaving({ BAR: false, RESTAURANT: false });

    if (failed.length) {
      throw new Error(`Product saved, but outlet settings did not all save. ${failed.join("; ")}`);
    }
  };

  const validate = () => {
    const e = {};

    if (!form.biteCategoryId) {
      e.biteCategoryId = "Required";
    }

    if (!form.biteName?.trim()) {
      e.biteName = "Required";
    }

    if (!form.itemSection) {
      e.itemSection = "Required";
    }

    if (form.barcode && form.barcode.trim().length > 100) {
      e.barcode = "Barcode cannot exceed 100 characters";
    }

    if (form.sellingPrice == null || Number(form.sellingPrice) < 0) {
      e.sellingPrice = "Must be ≥ 0";
    }

    if (form.costPrice == null || Number(form.costPrice) < 0) {
      e.costPrice = "Must be ≥ 0";
    }

    if (form.lowStockLevel == null || Number(form.lowStockLevel) < 0) {
      e.lowStockLevel = "Must be ≥ 0";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    if (editingId && (!outletSettings || outletLoading || outletLoadError)) {
      toast.error("Load outlet settings before saving this item.");
      return;
    }
    const parsedOverrides = validateOutletOverrides();
    if (!parsedOverrides) return;

    setSaving(true);

    const payload = {
      biteCategoryId: Number(form.biteCategoryId),
      biteName: form.biteName.trim(),
      barcode: form.barcode?.trim() || null,
      sellingPrice: Number(form.sellingPrice || 0),
      itemSection: form.itemSection || "FOOD",
      stockTracked: Boolean(form.stockTracked),
      requiresKot: Boolean(form.requiresKot),
      costPrice: Number(form.costPrice || 0),
      lowStockLevel: Number(form.lowStockLevel || 0),
    };

    try {
      let targetBiteId = editingId || createdProductId;
      let newlyCreated = false;
      if (editingId) {
        await BiteProductsService.update(editingId, payload);
      } else if (!createdProductId) {
        const savedProduct = await BiteProductsService.create(payload);
        targetBiteId = resolveSavedBiteId(savedProduct);
        newlyCreated = true;
        if (!targetBiteId) {
          throw new Error("Item was created, but the saved BiteProduct ID was not returned. Outlet settings were not saved.");
        }
        setCreatedProductId(targetBiteId);
      }

      if (newlyCreated && imageFile) {
        if (!targetBiteId) {
          toast.warning(
            "Item saved, but image was not uploaded because the saved item ID was not returned."
          );
        } else {
          await BiteProductsService.uploadImage(targetBiteId, imageFile);
        }
      } else if (!newlyCreated && removeImage && targetBiteId) {
        await BiteProductsService.removeImage(targetBiteId);
      }

      await persistOutletSettings(targetBiteId, parsedOverrides);

      toast.success(imageFile ? "Saved with image" : "Saved");
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      setCreatedProductId(null);
      setImageFile(null);
      setImagePreviewUrl("");
      setExistingImageUrl("");
      setRemoveImage(false);
      setOutletSettings(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (r) => {
    try {
      await BiteProductsService.setStatus(
        r.biteId,
        r.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
      );

      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div>
      <PageHeader
        title="Food & Beverages"
        subtitle="Manage food, bites, beverages and other restaurant items."
        actions={
          can.create && (
            <Button
              onClick={openCreate}
              className="bg-navy-800 hover:bg-navy-900 text-white"
              data-testid="bites-add"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Item
            </Button>
          )
        }
      />

      <DataTable
        loading={loading}
        rows={rows.map((r) => ({ ...r, key: r.biteId }))}
        testId="bites-table"
        searchable
        searchKeys={[
          "biteName",
          "biteCategoryName",
          "itemSection",
          "barcode",
          "status",
        ]}
        searchPlaceholder="Search items by name, category, section or barcode…"
        columns={[
          {
            key: "biteName",
            header: "Name",
          },
          {
            key: "biteCategoryName",
            header: "Category",
          },
          {
            key: "itemSection",
            header: "Section",
            render: (r) => {
              const section = r.itemSection || "FOOD";

              if (section === "FOOD") return "Food & Bites";
              if (section === "BEVERAGE") return "Beverage";
              if (section === "GOODS") return "Goods";

              return section;
            },
          },
          {
            key: "sellingPrice",
            header: "Selling Price",
            render: (r) => currency(r.sellingPrice),
          },
          {
            key: "costPrice",
            header: "Cost",
            render: (r) => currency(r.costPrice || 0),
          },
          {
            key: "stockTracked",
            header: "Stock",
            render: (r) => <StockBadge value={r.stockTracked} />,
          },
          {
            key: "lowStockLevel",
            header: "Low Stock",
            render: (r) => r.lowStockLevel ?? 0,
          },
          {
            key: "status",
            header: "Status",
            render: (r) => <StatusBadge value={r.status} />,
          },
          {
            key: "actions",
            header: "",
            render: (r) =>
              can.modify ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(r)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggle(r)}
                  >
                    <Power className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <span className="text-slate-300 text-xs">—</span>
              ),
          },
        ]}
      />

      <FormModal
        open={open}
        onOpenChange={handleModalOpenChange}
        title={editingId ? "Edit item" : "New item"}
        onSubmit={submit}
        loading={saving}
        size="lg"
      >
        <div className="max-h-[72vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ProductImagePicker
              previewUrl={imagePreviewUrl}
              selectedFile={imageFile}
              existingImageUrl={existingImageUrl}
              markedForRemoval={removeImage}
              onSelectFile={handleImageFileSelect}
              onClearSelectedFile={clearSelectedImageFile}
              onRemoveExistingImage={markExistingImageForRemoval}
              disabled={saving}
            />

            <SelectField
              label="Category"
              required
              value={form.biteCategoryId}
              onChange={(v) => setForm({ ...form, biteCategoryId: v })}
              options={cats.map((c) => ({
                value: c.biteCategoryId,
                label: c.categoryName,
              }))}
              error={errors.biteCategoryId}
              testId="bite-category"
            />

            <SelectField
              label="Section"
              required
              value={form.itemSection}
              onChange={(v) => setForm({ ...form, itemSection: v })}
              options={[
                { value: "FOOD", label: "Food & Bites" },
                { value: "BEVERAGE", label: "Beverage" },
                { value: "GOODS", label: "Goods" },
              ]}
              error={errors.itemSection}
              testId="bite-section"
            />

            <TextField
              label="Item name"
              required
              value={form.biteName}
              onChange={(v) => setForm({ ...form, biteName: v })}
              error={errors.biteName}
              testId="bite-name"
            />

            <TextField
              label="Barcode"
              value={form.barcode}
              onChange={(v) => setForm({ ...form, barcode: v })}
              error={errors.barcode}
              testId="bite-barcode"
            />

            <TextField
              label="Selling price"
              required
              type="number"
              value={form.sellingPrice}
              onChange={(v) => setForm({ ...form, sellingPrice: v })}
              error={errors.sellingPrice}
              testId="bite-price"
            />

            <TextField
              label="Cost price"
              type="number"
              value={form.costPrice}
              onChange={(v) => setForm({ ...form, costPrice: v })}
              error={errors.costPrice}
              testId="bite-cost"
            />

            <SelectField
              label="Stock tracked"
              value={form.stockTracked ? "YES" : "NO"}
              onChange={(v) =>
                setForm({
                  ...form,
                  stockTracked: v === "YES",
                })
              }
              options={[
                { value: "NO", label: "No" },
                { value: "YES", label: "Yes" },
              ]}
              testId="bite-stock-tracked"
            />

            <SelectField
              label="Requires KOT"
              value={form.requiresKot ? "YES" : "NO"}
              onChange={(v) =>
                setForm({
                  ...form,
                  requiresKot: v === "YES",
                })
              }
              options={[
                { value: "NO", label: "No" },
                { value: "YES", label: "Yes" },
              ]}
              testId="bite-requires-kot"
            />

            <TextField
              label="Low stock level"
              type="number"
              value={form.lowStockLevel}
              onChange={(v) => setForm({ ...form, lowStockLevel: v })}
              error={errors.lowStockLevel}
              testId="bite-low-stock"
            />
          </div>

          <section className="mt-6 border-t border-slate-200 pt-5">
              <h3 className="font-semibold text-slate-900">Outlet Availability &amp; Pricing</h3>
              <p className="text-sm text-slate-500 mt-1">
                Configure where this product can be sold and its price at each outlet. Stock remains shared.
              </p>

              {outletLoading ? (
                <div className="py-6 text-center text-sm text-slate-500">Loading outlet settings…</div>
              ) : outletLoadError ? (
                <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm">
                  <p className="text-rose-700">{outletLoadError}</p>
                  <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => loadOutletSettings(editingId)}>
                    Retry
                  </Button>
                </div>
              ) : outletSettings ? (
                <div className="mt-4 grid grid-cols-1 gap-3">
                  {OUTLETS.map(({ code, name }) => {
                    const setting = outletSettings[code];
                    if (!setting) return null;
                    const effectivePrice = setting.sellingPriceOverride === "" || setting.sellingPriceOverride == null
                      ? form.sellingPrice
                      : setting.sellingPriceOverride;
                    return (
                      <div key={code} className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="font-semibold text-slate-800">{name}</h4>
                          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <Switch
                              checked={Boolean(setting.enabled)}
                              disabled={Boolean(outletSaving[code]) || saving}
                              onCheckedChange={(enabled) => setOutletSettings((previous) => ({
                                ...previous,
                                [code]: { ...previous[code], enabled },
                              }))}
                              aria-label={`${name} enabled`}
                            />
                            Available at {name === "RESTAURANT" ? "Restaurant" : "BAR"}
                          </label>
                        </div>

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-slate-500">Master Price</div>
                            <div className="font-medium text-slate-800">{currency(form.sellingPrice)}</div>
                          </div>
                          <label className="space-y-1.5">
                            <span className="block text-xs font-medium text-slate-600">Price Override</span>
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              value={setting.sellingPriceOverride ?? ""}
                              placeholder="Use master price"
                              disabled={Boolean(outletSaving[code]) || saving}
                              onChange={(event) => setOutletSettings((previous) => ({
                                ...previous,
                                [code]: { ...previous[code], sellingPriceOverride: event.target.value },
                              }))}
                              aria-label={`${name} price override`}
                            />
                          </label>
                          <div className="sm:col-span-2">
                            <div className="text-slate-500">Effective Price</div>
                            <div className="font-semibold text-slate-900">{currency(effectivePrice)}</div>
                          </div>
                        </div>

                        {outletSaveErrors[code] && <p className="mt-3 text-xs text-rose-600">{outletSaveErrors[code]}</p>}
                        {outletSaving[code] && <p className="mt-3 text-xs text-slate-500">Saving {name} settings…</p>}
                      </div>
                    );
                  })}
                </div>
              ) : null}
          </section>
        </div>
      </FormModal>
    </div>
  );
};
